import { useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import { Modal } from './Modal';
import { CLIENT_COPY } from '../../../../../shared/client-copy.js';
import './DeleteAccountModal.css';

/** The dialog's title and body: one source for all three clients (src/shared/client-copy.js legal.delete_account, 2026-10-21 Riley). */
const DELETE_COPY = CLIENT_COPY.legal.delete_account;

export interface DeleteAccountModalProps {
  onClose: () => void;
}

/**
 * Web confirmation for account deletion — the authenticated session plus this
 * modal is the confirmation (see edge/account-deletion/account-deletion.js).
 * Confirming calls useSession().deleteAccount(), a soft delete: the account is
 * retained 30 days, then AccountPurgeWorker hard-deletes it.
 *
 * The copy names both ways back and, more importantly, the way that is NOT one:
 * signing in again used to cancel the deletion silently, and this modal said
 * only "reply RESTORE" — a promise nothing implemented after the v1.2 ingress
 * teardown. Both halves are true now (inbound RESTORE works again, sign-in
 * restores nothing), so the copy states them.
 */
export function DeleteAccountModal({ onClose }: DeleteAccountModalProps): ReactElement {
  const { deleteAccount } = useSession();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Done: said here, before the page is left (a toast set just before the reload to / was never seen).
  const [done, setDone] = useState(false);
  const leave = (): void => { window.location.href = '/'; };

  const confirm = async (): Promise<void> => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      await deleteAccount();
      setDone(true);
    } catch {
      setError("Couldn't delete your account right now. Try again.");
      trackFailed('account_delete', 'error');
      setSending(false);
    }
  };

  if (done) {
    return (
      <Modal onClose={leave} labelledBy="delete-account-title" className="delete-account-modal">
        <h2 id="delete-account-title" className="delete-account-title">Your account is scheduled for deletion</h2>
        <div className="delete-account-body">
          <p>{DELETE_COPY.body[1]}</p>
        </div>
        <div className="delete-account-actions">
          <button type="button" className="ember-btn ember-btn-primary" onClick={leave}>OK</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={onClose}
      labelledBy="delete-account-title"
      className="delete-account-modal"
      dismissible={!sending}
    >
      <h2 id="delete-account-title" className="delete-account-title">{DELETE_COPY.title}</h2>
      <div className="delete-account-body">
        {DELETE_COPY.body.map((p) => <p key={p}>{p}</p>)}
      </div>
      {error && <p className="delete-account-error" role="alert">{error}</p>}
      <div className="delete-account-actions">
        <button type="button" className="ember-btn ember-btn-ghost" data-log="account_delete_cancel" onClick={onClose} disabled={sending}>
          Cancel
        </button>
        <button
          type="button"
          className="ember-btn ember-btn-danger"
          disabled={sending}
          data-log="account_delete_confirm"
          onClick={() => { void confirm(); }}
        >
          {sending ? 'Deleting…' : 'Delete account'}
        </button>
      </div>
    </Modal>
  );
}
